import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "lib/api";
import { useDashboardSettings } from "../settings/store";
import { dashboardActividadesQueryKeys as keys } from "./keys";

const JSON_HEADERS = { "Content-Type": "application/json" };

function jsonRequest(method, payload) {
  if (payload === undefined) return { method };
  return { method, body: JSON.stringify(payload), headers: JSON_HEADERS };
}

function invalidateActividadesList(qc) {
  return qc.invalidateQueries({ queryKey: ["dashboard", "actividades"] });
}

function invalidateActividadDetail(qc) {
  return qc.invalidateQueries({ queryKey: ["dashboard", "actividad"] });
}

function invalidateActividadById(qc, periodoId, actividadId) {
  if (!actividadId) return Promise.resolve();
  return qc.invalidateQueries({ queryKey: keys.actividad(periodoId, actividadId) });
}

function invalidateActividadListAndDetail(qc) {
  return Promise.all([
    invalidateActividadesList(qc),
    invalidateActividadDetail(qc),
    qc.invalidateQueries({ queryKey: ["dashboard", "fotos"] }),
  ]);
}

function invalidateActividadMutationScope(qc, scopeKey, periodoId, actividadId) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: scopeKey }),
    invalidateActividadesList(qc),
    invalidateActividadById(qc, periodoId, actividadId),
    qc.invalidateQueries({ queryKey: ["dashboard", "instructor-activity-overview"] }),
    qc.invalidateQueries({ queryKey: ["dashboard", "fotos"] }),
  ]);
}

export function useCreateDashboardActividad() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => api("/dashboard/club/actividades", { method: "POST" }, { periodoId: s.periodoId }),
    onSuccess: () => invalidateActividadesList(qc),
  });
}

export function useDeleteDashboardActividad() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (actividadId) => api(`/dashboard/club/actividades/${actividadId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: () => {
      const invalidation = invalidateActividadesList(qc);
      qc.removeQueries({ queryKey: ["dashboard", "actividad"] });
      return invalidation;
    },
  });
}

export function usePatchDashboardActividad() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, patch }) =>
      api(`/dashboard/club/actividades/${actividadId}`, jsonRequest("PATCH", patch), { periodoId: s.periodoId }),

    onMutate: async ({ actividadId, patch }) => {
      await qc.cancelQueries({ queryKey: ["dashboard", "actividad"] });

      const key = keys.actividad(s.periodoId, actividadId);
      const prev = qc.getQueryData(key);
      if (prev && patch && typeof patch === "object") {
        qc.setQueryData(key, { ...prev, ...patch });
      }

      return { prev, key };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.prev && ctx?.key) qc.setQueryData(ctx.key, ctx.prev);
    },

    onSuccess: () => invalidateActividadListAndDetail(qc),
  });
}

export function useSetDashboardActividadCover() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, payload }) =>
      api(`/dashboard/club/actividades/${actividadId}/cover`, jsonRequest("POST", payload), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => Promise.all([
      invalidateActividadListAndDetail(qc),
      invalidateActividadById(qc, s.periodoId, vars?.actividadId),
    ]),
  });
}

export function useAddDashboardActividadPhoto() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, payload }) =>
      api(`/dashboard/club/actividades/${actividadId}/photos`, jsonRequest("POST", payload), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => Promise.all([
      invalidateActividadDetail(qc),
      invalidateActividadById(qc, s.periodoId, vars?.actividadId),
    ]),
  });
}

export function useDeleteDashboardActividadPhoto() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, imageId }) =>
      api(`/dashboard/club/actividades/${actividadId}/photos/${imageId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => Promise.all([
      invalidateActividadDetail(qc),
      invalidateActividadById(qc, s.periodoId, vars?.actividadId),
    ]),
  });
}

export function useCreateDashboardActividadEncargo() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, payload }) =>
      api(`/dashboard/club/actividades/${actividadId}/encargos`, jsonRequest("POST", payload), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-encargos"], s.periodoId, vars?.actividadId),
  });
}

export function usePatchDashboardActividadEncargo() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, encargoId, patch }) =>
      api(`/dashboard/club/actividades/${actividadId}/encargos/${encargoId}`, jsonRequest("PATCH", patch), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-encargos"], s.periodoId, vars?.actividadId),
  });
}

export function useDeleteDashboardActividadEncargo() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, encargoId }) =>
      api(`/dashboard/club/actividades/${actividadId}/encargos/${encargoId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-encargos"], s.periodoId, vars?.actividadId),
  });
}

export function useAddDashboardActividadAsistenciaSocio() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, socioId }) =>
      api(`/dashboard/club/actividades/${actividadId}/asistencia/socios`, jsonRequest("POST", { socioId }), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-asistencia-socios"], s.periodoId, vars?.actividadId),
  });
}

export function useRemoveDashboardActividadAsistenciaSocio() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, socioId }) =>
      api(`/dashboard/club/actividades/${actividadId}/asistencia/socios/${socioId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-asistencia-socios"], s.periodoId, vars?.actividadId),
  });
}

export function useAddDashboardActividadAsistenciaInstructor() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, instructorId, puntualidad }) =>
      api(`/dashboard/club/actividades/${actividadId}/asistencia/instructores`, jsonRequest("POST", { instructorId, puntualidad }), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-asistencia-instructores"], s.periodoId, vars?.actividadId),
  });
}

export function usePatchDashboardActividadAsistenciaInstructor() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, instructorId, patch }) =>
      api(`/dashboard/club/actividades/${actividadId}/asistencia/instructores/${instructorId}`, jsonRequest("PATCH", patch), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-asistencia-instructores"], s.periodoId, vars?.actividadId),
  });
}

export function useRemoveDashboardActividadAsistenciaInstructor() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, instructorId }) =>
      api(`/dashboard/club/actividades/${actividadId}/asistencia/instructores/${instructorId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-asistencia-instructores"], s.periodoId, vars?.actividadId),
  });
}

export function useCreateDashboardActividadRemplazo() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, payload }) =>
      api(`/dashboard/club/actividades/${actividadId}/remplazos`, jsonRequest("POST", payload), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-remplazos"], s.periodoId, vars?.actividadId),
  });
}

export function usePatchDashboardActividadRemplazo() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, remplazoRowId, patch }) =>
      api(`/dashboard/club/actividades/${actividadId}/remplazos/${remplazoRowId}`, jsonRequest("PATCH", patch), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-remplazos"], s.periodoId, vars?.actividadId),
  });
}

export function useDeleteDashboardActividadRemplazo() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, remplazoRowId }) =>
      api(`/dashboard/club/actividades/${actividadId}/remplazos/${remplazoRowId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-remplazos"], s.periodoId, vars?.actividadId),
  });
}

export function useAddDashboardActividadPreceptoria() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, instructorId, socioId }) =>
      api(`/dashboard/club/actividades/${actividadId}/preceptorias`, jsonRequest("POST", { instructorId, socioId }), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-preceptorias"], s.periodoId, vars?.actividadId),
  });
}

export function useRemoveDashboardActividadPreceptoria() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, instructorId, socioId }) =>
      api(`/dashboard/club/actividades/${actividadId}/preceptorias`, jsonRequest("DELETE", { instructorId, socioId }), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-preceptorias"], s.periodoId, vars?.actividadId),
  });
}

export function useUpsertDashboardActividadMaterial() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, materialId, cantidad }) =>
      api(`/dashboard/club/actividades/${actividadId}/materiales/${materialId}`, jsonRequest("PUT", { cantidad }), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-materiales"], s.periodoId, vars?.actividadId),
  });
}

export function useDeleteDashboardActividadMaterial() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, materialId }) =>
      api(`/dashboard/club/actividades/${actividadId}/materiales/${materialId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-materiales"], s.periodoId, vars?.actividadId),
  });
}

export function useCreateDashboardActividadGasto() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, payload }) =>
      api(`/dashboard/club/actividades/${actividadId}/gastos`, jsonRequest("POST", payload), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-gastos"], s.periodoId, vars?.actividadId),
  });
}

export function usePatchDashboardActividadGasto() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, gastoId, patch }) =>
      api(`/dashboard/club/actividades/${actividadId}/gastos/${gastoId}`, jsonRequest("PATCH", patch), { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-gastos"], s.periodoId, vars?.actividadId),
  });
}

export function useDeleteDashboardActividadGasto() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ actividadId, gastoId }) =>
      api(`/dashboard/club/actividades/${actividadId}/gastos/${gastoId}`, { method: "DELETE" }, { periodoId: s.periodoId }),
    onSuccess: (_data, vars) => invalidateActividadMutationScope(qc, ["dashboard", "actividad-gastos"], s.periodoId, vars?.actividadId),
  });
}


